// frontend/src/components/auth/ResetPasswordForm.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader2, KeyRound } from 'lucide-react';
import { validatePasswordStrength } from '@/utils/auth';
import { authService } from '@/lib/api/authService';

interface ResetPasswordFormProps {
  className?: string;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ className = '' }) => {
  const [formData, setFormData] = useState({
    password: '',
    password_confirm: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('Invalid or missing reset token');
    } else {
      setTokenValid(true);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await authService.confirmPasswordReset({
        token,
        password: formData.password,
        password_confirm: formData.password_confirm
      });
      
      if (result.success) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Password reset successful! You can now log in with your new password.',
              type: 'success'
            }
          });
        }, 3000);
      } else {
        if (result.errors) {
          // Display first error
          const firstError = Array.isArray(result.errors) 
            ? result.errors[0] 
            : Object.values(result.errors)[0];
          setError(Array.isArray(firstError) ? firstError[0] : firstError);
        } else {
          setError(result.message || 'Password reset failed. Please try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const passwordValidation = validatePasswordStrength(formData.password);
  const passwordsMatch = formData.password === formData.password_confirm;
  const canSubmit = passwordValidation.isValid && passwordsMatch && formData.password_confirm;

  // Invalid token state
  if (tokenValid === false) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Invalid Reset Link
          </h2>
          <p className="mt-4 text-gray-600">
            The password reset link is invalid or has expired. Please request a new password reset.
          </p>
          <div className="mt-8 space-y-3">
            <Link
              to="/forgot-password"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Request New Reset Link
            </Link>
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 animate-in zoom-in duration-500">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Password Reset Successful!
          </h2>
          <p className="mt-4 text-gray-600">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <div className="mt-6">
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary-600" />
            <p className="mt-2 text-sm text-gray-500">Redirecting to login page...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <KeyRound className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your new password below. Make sure it's strong and secure.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200 animate-in slide-in-from-top-2 duration-300">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* New Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleInputChange}
                className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm transition-colors"
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`flex items-center px-2 py-1 rounded ${passwordValidation.minLength ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {passwordValidation.minLength ? '✓' : '✗'} 8+ characters
                  </span>
                  <span className={`flex items-center px-2 py-1 rounded ${passwordValidation.hasUppercase ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {passwordValidation.hasUppercase ? '✓' : '✗'} Uppercase
                  </span>
                  <span className={`flex items-center px-2 py-1 rounded ${passwordValidation.hasLowercase ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {passwordValidation.hasLowercase ? '✓' : '✗'} Lowercase
                  </span>
                  <span className={`flex items-center px-2 py-1 rounded ${passwordValidation.hasNumber ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {passwordValidation.hasNumber ? '✓' : '✗'} Number
                  </span>
                </div>
                
                {/* Strength meter */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordValidation.strength === 'weak' ? 'bg-red-500 w-1/4' :
                      passwordValidation.strength === 'fair' ? 'bg-yellow-500 w-2/4' :
                      passwordValidation.strength === 'good' ? 'bg-blue-500 w-3/4' :
                      passwordValidation.strength === 'strong' ? 'bg-green-500 w-full' : 'w-0'
                    }`}
                  />
                </div>
                <p className={`text-xs ${
                  passwordValidation.strength === 'weak' ? 'text-red-600' :
                  passwordValidation.strength === 'fair' ? 'text-yellow-600' :
                  passwordValidation.strength === 'good' ? 'text-blue-600' :
                  passwordValidation.strength === 'strong' ? 'text-green-600' : 'text-gray-600'
                }`}>
                  Password strength: {passwordValidation.strength}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password_confirm"
                name="password_confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={formData.password_confirm}
                onChange={handleInputChange}
                className={`appearance-none relative block w-full pl-10 pr-10 py-3 border ${
                  formData.password_confirm && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm transition-colors`}
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
            {formData.password_confirm && !passwordsMatch && (
              <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
            )}
            {formData.password_confirm && passwordsMatch && (
              <p className="mt-1 text-sm text-green-600 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                Passwords match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !canSubmit}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Resetting password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          {/* Back to Login */}
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              Back to login
            </Link>
          </div>
        </form>

        {/* Security Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Lock className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Security Tips
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Use a unique password you haven't used elsewhere</li>
                  <li>Consider using a password manager</li>
                  <li>Don't share your password with anyone</li>
                  <li>Log out from shared devices</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Need help?{' '}
            <Link to="/contact" className="underline hover:text-gray-700">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;